/**
 * What a recorded view key is called, and which part of the space it belongs to.
 *
 * ── Derived, not written a second time ───────────────────────────────────────────────────────
 * The rollups group by view key — `ta-action-boards`, `equity-calculator` — which is the first
 * segment of a path and nobody's name for anything. The sidebar already holds the real names and
 * the grouping, so this reads them from the nav config: a page renamed in the sidebar is renamed
 * here, and a page added to a group lands in the right area of the board without a second edit.
 * A hand-kept label table would be wrong the first time somebody renamed a nav item, and wrong
 * silently — a board still showing the old name looks correct.
 *
 * Anything the nav does not know (the public booking page, events recorded before a route
 * existed) degrades to a prettified slug rather than disappearing.
 */
import { peopleSpaceConfig, sidebarGroups } from '../../config';

export interface PageMeta {
  /** The view key as the rollups store it. */
  key: string;
  /** The label the sidebar uses, e.g. "TA Action Boards". */
  label: string;
  /**
   * The narrowest bold heading the sidebar files this page under — the SUBGROUP where there is
   * one ("Candidate Communication"), otherwise the group ("Talent Acquisition").
   *
   * It used to be the group alone, which is why the filter did not match the sidebar: all five
   * pages under Candidate Communication reported "Talent Acquisition", and the heading a reader
   * can see above them was not offered at all.
   */
  area: string;
  /** The group above `area`, when `area` is a subgroup — so filtering by the parent still finds it. */
  parentArea: string | null;
  /** Where to go and look at the page itself, or null when there is nowhere to send anyone. */
  path: string | null;
}

/** Pages outside any sidebar group, and the unattributed bucket, need an area to sort under. */
export const OTHER_AREA = 'Elsewhere';

/**
 * Client-side mirror of `viewKey` in `proxy-server/lib/usageStore.js`.
 *
 * Only ever applied to the app's OWN nav paths here — the keys that arrive from the API are
 * already reduced server-side. Kept in step with the server rule so the nav path and the recorded
 * key for the same page produce the same string; if they ever diverge, the page silently loses
 * its label rather than breaking, which is why the fallback below exists.
 */
export function viewKeyOf(path: string): string {
  const [first] = path.split('?')[0].split('#')[0].split('/').filter(Boolean);
  if (!first) return 'home';
  return first.toLowerCase().replace(/[^a-z0-9_-]/g, '-').slice(0, 40) || 'unknown';
}

/**
 * View keys that are the same page under two paths.
 *
 * The Log is reachable as `/interviews` (the nav item, kept for bookmarks) and `/log` (what it is
 * called), so its events land under two keys. Shown apart they would read as two half-used pages
 * — the honest answer is one page with all of its traffic.
 */
export const VIEW_ALIASES: Record<string, string> = { log: 'interviews' };

/** `first-round` → `First round`. Tab slugs are URL segments; the board is read by people. */
export function prettify(slug: string): string {
  const words = slug.replace(/[-_]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : slug;
}

/**
 * Tab labels the slug cannot produce.
 *
 * `prettify` turns `sla` into "Sla", which is the acronym spelled as a word and reads as a typo.
 * An override table rather than a general acronym rule: a rule would have to guess, and guessing
 * wrong on a page name is more visible than the handful of entries it would save.
 */
const TAB_LABELS: Record<string, string> = {
  'ta-action-boards/sla': 'SLA',
};

/** How a tab is written on the board, e.g. `ta-action-boards` + `sla` → "SLA". */
export function tabLabel(view: string, tab: string): string {
  return TAB_LABELS[`${view}/${tab}`] ?? prettify(tab);
}

/**
 * ⚠️ Built on FIRST USE, never at module load.
 *
 * `config.ts` imports the space component, which reaches this file through the Usage Analytics
 * view — so this module can be evaluated while `config.ts` is still initialising, and reading
 * `sidebarGroups` at the top level throws on `undefined`. It did, and it took out an unrelated
 * suite. By the time anything calls `pageMeta`, the cycle has resolved.
 */
let metaByKey: Map<string, PageMeta> | null = null;

function registry(): Map<string, PageMeta> {
  if (metaByKey) return metaByKey;
  const groupName = new Map(sidebarGroups.map((group) => [group.id, group.name]));
  const subgroupName = new Map(
    sidebarGroups.flatMap((group) => (group.subgroups ?? []).map((sub) => [sub.id, sub.name]))
  );
  metaByKey = new Map(
    peopleSpaceConfig.navItems.map((item) => {
      const group = (item.group && groupName.get(item.group)) || null;
      const subgroup = (item.subgroup && subgroupName.get(item.subgroup)) || null;
      return [
        viewKeyOf(item.path),
        {
          key: viewKeyOf(item.path),
          label: item.name,
          area: subgroup || group || OTHER_AREA,
          parentArea: subgroup ? group : null,
          path: item.path,
        },
      ];
    })
  );
  return metaByKey;
}

/**
 * Every bold heading the sidebar shows, in sidebar order, each with the group it sits under.
 *
 * Groups AND subgroups, because both are bold headings and both are how somebody describes where
 * a page lives. The filter renders only the ones that actually hold a page in the range.
 */
export function sidebarAreas(): { name: string; parent: string | null }[] {
  const out: { name: string; parent: string | null }[] = [];
  for (const group of sidebarGroups) {
    if (group.items.length === 0) continue;
    out.push({ name: group.name, parent: null });
    for (const sub of group.subgroups ?? []) out.push({ name: sub.name, parent: group.name });
  }
  out.push({ name: OTHER_AREA, parent: null });
  return out;
}

/**
 * Pages that record events but have no nav item, because nobody navigates to them from the
 * sidebar. `unattributed` is not a page at all — it is the remainder the section rollup cannot
 * place, and calling it anything else would be a lie about where the work happened.
 */
const UNLISTED: Record<string, PageMeta> = {
  book: {
    key: 'book',
    label: 'Candidate booking page',
    area: 'Talent Acquisition',
    parentArea: null,
    path: null,
  },
  unattributed: {
    key: 'unattributed',
    label: 'Not attributed to a page',
    area: OTHER_AREA,
    parentArea: null,
    path: null,
  },
  unknown: {
    key: 'unknown',
    label: 'Unrecognised page',
    area: OTHER_AREA,
    parentArea: null,
    path: null,
  },
};

export function pageMeta(key: string): PageMeta {
  const canonical = VIEW_ALIASES[key] ?? key;
  return (
    registry().get(canonical) ??
    UNLISTED[canonical] ?? {
      key: canonical,
      label: prettify(canonical),
      area: OTHER_AREA,
      parentArea: null,
      path: `/${canonical}`,
    }
  );
}


